/**
 * GERENCIA — cómo va el negocio, para quien pone el dinero.
 *
 * QUÉ LO SEPARA DEL DASHBOARD Y DE CAJA
 *
 * Caja contesta "¿cuánto entró hoy y cuadra el cajón?". Esta pantalla contesta
 * las cuatro preguntas de un socio: si vamos mejor que el periodo anterior, qué
 * especialidad tira, cuánta agenda se quedó vacía, y si crecemos o rotamos a
 * los mismos pacientes.
 *
 * TODO VA CONTRA EL PERIODO ANTERIOR
 *
 * Un número solo no dice nada. S/ 12.000 es bueno o malo según lo que fue el
 * mes pasado, y esa comparación hecha a mano es la que nadie hace nunca. Se
 * enseña el número anterior entero, no solo el porcentaje: un +0 % sobre cero
 * no es lo mismo que sobre diez mil.
 *
 * NÚMEROS SÍ, HISTORIAS NO
 *
 * Aquí no aparece ni un paciente por su nombre ni un diagnóstico. Ser socio no
 * es una razón clínica para abrir la historia de nadie.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  Receipt,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import {
  type Frecuente,
  type LineaReporte,
  type PorCobrar,
  type PuntoSerie,
  getGerencia,
} from "../api/reportes";
import { Alert, Card, CardBody, CardHeader } from "../components/ui";
import { hoyEnZona } from "../lib/fecha";
import { useZonaHoraria } from "../auth/TenantBrandingProvider";

const soles = (v: string | number) => `S/ ${Number(v).toFixed(2)}`;

/** Los rangos que se piden de verdad. Nadie escribe fechas para mirar el mes. */
type Atajo = "hoy" | "semana" | "mes" | "anio";

function rango(atajo: Atajo, hoy: string): { desde: string; hasta: string } {
  const d = new Date(`${hoy}T12:00:00Z`);
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  switch (atajo) {
    case "hoy":
      return { desde: hoy, hasta: hoy };
    case "semana": {
      // Semana que empieza el lunes: es como se habla de "esta semana".
      const dia = (d.getUTCDay() + 6) % 7;
      const lunes = new Date(d);
      lunes.setUTCDate(d.getUTCDate() - dia);
      return { desde: iso(lunes), hasta: hoy };
    }
    case "anio":
      return { desde: `${hoy.slice(0, 4)}-01-01`, hasta: hoy };
    default:
      return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
  }
}

export function GerenciaPage() {
  const tz = useZonaHoraria();
  const hoy = hoyEnZona(tz);
  const [atajo, setAtajo] = useState<Atajo>("mes");
  const { desde, hasta } = useMemo(() => rango(atajo, hoy), [atajo, hoy]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["gerencia", desde, hasta],
    queryFn: () => getGerencia(desde, hasta),
  });

  if (error) return <Alert>No se pudieron cargar los números.</Alert>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Gerencia</h1>
        <p className="mt-1 text-sm text-slate-500">
          Indicadores del periodo, contra el periodo anterior de igual duración.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {([
          ["hoy", "Hoy"],
          ["semana", "Esta semana"],
          ["mes", "Este mes"],
          ["anio", "Este año"],
        ] as [Atajo, string][]).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setAtajo(id)}
            aria-current={atajo === id ? "page" : undefined}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              atajo === id
                ? "border-[color:var(--color-link)] text-[color:var(--color-link)]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <p className="text-sm text-slate-400">Cargando…</p>
      ) : (
        <>
          <p className="text-xs text-slate-400">
            Periodo {data.desde} a {data.hasta} · base comparativa{" "}
            {data.comparado_desde} a {data.comparado_hasta}
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Comparativa
              icono={<Wallet className="h-5 w-5" />}
              etiqueta="Ingresos"
              actual={soles(data.ingresos.actual)}
              anterior={soles(data.ingresos.anterior)}
              variacion={variacion(data.ingresos.actual, data.ingresos.anterior)}
            />
            {/* Entre ingresos y cobros a propósito: es el que explica a los
                otros dos. Subir ingresos con el ticket plano es que vino más
                gente; subirlos con el ticket disparado y los cobros planos es
                otra cosa muy distinta. */}
            <Comparativa
              icono={<Receipt className="h-5 w-5" />}
              etiqueta="Ticket promedio"
              actual={soles(data.ticket.actual)}
              anterior={soles(data.ticket.anterior)}
              variacion={variacion(data.ticket.actual, data.ticket.anterior)}
            />
            <Comparativa
              icono={<CalendarDays className="h-5 w-5" />}
              etiqueta="Cobros"
              actual={data.cobros.actual}
              anterior={data.cobros.anterior}
              variacion={variacion(data.cobros.actual, data.cobros.anterior)}
            />
            <Comparativa
              icono={<Users className="h-5 w-5" />}
              etiqueta="Pacientes nuevos"
              actual={String(data.pacientes.nuevos)}
              anterior={`${data.pacientes.recurrentes} ya venían`}
              variacion={null}
            />
          </div>

          <DiaADia puntos={data.serie} paso={data.paso} />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Ingresos por línea de servicio</h2>
              </CardHeader>
              <CardBody className="space-y-2">
                {Object.entries(data.por_origen).length === 0 && (
                  <p className="text-sm text-slate-500">Sin ingresos registrados en el periodo.</p>
                )}
                {Object.entries(data.por_origen).map(([nombre, monto]) => (
                  <Barra
                    key={nombre}
                    nombre={nombre}
                    valor={Number(monto)}
                    total={Number(data.ingresos.actual)}
                    derecha={soles(monto)}
                  />
                ))}
                <div className="border-t border-slate-100 pt-2 text-xs text-slate-500">
                  {Object.entries(data.por_metodo)
                    .map(([m, v]) => `${m.toLowerCase()} ${soles(v)}`)
                    .join(" · ")}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Ocupación de agenda</h2>
              </CardHeader>
              <CardBody>
                <p className="text-3xl font-bold text-slate-900">
                  {data.agenda.asistencia}%
                </p>
                <p className="text-sm text-slate-500">
                  tasa de asistencia sobre las citas programadas no canceladas
                </p>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <Dato etiqueta="Citas" valor={data.agenda.citas} />
                  <Dato etiqueta="Atendidas" valor={data.agenda.atendidas} />
                  {/* Estas dos son dinero que no entró: se marcan aunque estén
                      en cero, porque el día que suban hay que verlas. */}
                  <Dato etiqueta="No asistió" valor={data.agenda.no_asistio} alerta />
                  <Dato etiqueta="Canceladas" valor={data.agenda.canceladas} />
                </div>
              </CardBody>
            </Card>

            <PendienteDeCobro datos={data.por_cobrar} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Ranking titulo="Ingresos por especialidad" lineas={data.por_especialidad} />
            <Ranking titulo="Producción por profesional" lineas={data.por_profesional} />
          </div>

          <Frecuentes lineas={data.frecuentes} />
        </>
      )}
    </div>
  );
}

/**
 * DÍA A DÍA.
 *
 * Aquí hubo un gráfico de barras y no servía para nada: un médico que dirige
 * su policlínico no quiere estimar una altura, quiere LEER cuánto entró el
 * martes. Así que números, en una tabla, con el mejor día marcado.
 *
 * Solo salen los días con movimiento. Los vacíos se cuentan abajo en una
 * línea: ocupan una fila cada uno y no aportan nada que no diga ese recuento.
 */
function DiaADia({ puntos, paso }: { puntos: PuntoSerie[]; paso: string }) {
  const conMovimiento = puntos.filter(
    (p) => Number(p.monto) > 0 || p.atendidos > 0,
  );
  const vacios = puntos.length - conMovimiento.length;
  const max = Math.max(...puntos.map((p) => Number(p.monto)), 0);

  const nombrePaso = paso === "mes" ? "mes" : paso === "semana" ? "semana" : "día";
  const totalAtendidos = puntos.reduce((a, p) => a + p.atendidos, 0);
  const totalIngresos = puntos.reduce((a, p) => a + Number(p.monto), 0);

  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">
          Detalle por {nombrePaso}
        </h2>
      </CardHeader>
      <CardBody className="p-0">
        {conMovimiento.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">Sin movimiento registrado en el periodo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2 font-medium">
                    {nombrePaso === "día" ? "Día" : nombrePaso}
                  </th>
                  <th className="px-4 py-2 text-right font-medium">Atendidos</th>
                  <th className="px-4 py-2 text-right font-medium">Cobros</th>
                  <th className="px-4 py-2 text-right font-medium">Ingresos</th>
                </tr>
              </thead>
              <tbody>
                {conMovimiento.map((p) => {
                  const v = Number(p.monto);
                  const esMejor = v > 0 && v === max;
                  return (
                    <tr
                      key={p.desde}
                      className={`border-b border-slate-50 last:border-0 ${
                        esMejor ? "bg-[color-mix(in_srgb,var(--color-link)_6%,white)]" : ""
                      }`}
                    >
                      <td className="px-4 py-2 text-slate-700">
                        {p.etiqueta}
                        {esMejor && (
                          <span className="ml-2 text-xs font-medium text-[color:var(--color-link)]">
                            máximo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {p.atendidos || "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                        {p.cobros || "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium text-slate-900">
                        {v > 0 ? soles(p.monto) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 font-semibold text-slate-900">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums">{totalAtendidos}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {puntos.reduce((a, p) => a + p.cobros, 0)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {soles(totalIngresos)}
                  </td>
                </tr>
              </tfoot>
            </table>
            {vacios > 0 && (
              <p className="px-4 py-2 text-xs text-slate-400">
                Se omiten {vacios} {vacios === 1 ? nombrePaso : `${nombrePaso}s`} sin
                movimiento registrado.
              </p>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * LOS QUE MÁS VUELVEN.
 *
 * La pregunta de quien dirige es quién sostiene la consulta. Va el nombre,
 * las visitas y lo que dejaron: nada de su historia clínica, que para eso
 * hace falta ser su médico y no su socio.
 */
function Frecuentes({ lineas }: { lineas: Frecuente[] }) {
  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Pacientes recurrentes</h2>
      </CardHeader>
      <CardBody className="space-y-2">
        {lineas.length === 0 ? (
          <p className="text-sm text-slate-500">
            Ningún paciente registró más de una atención en el periodo.
          </p>
        ) : (
          lineas.map((f) => (
            <div
              key={f.nombre}
              className="flex items-baseline justify-between gap-3 border-b border-slate-50 pb-2 last:border-0 last:pb-0"
            >
              <span className="truncate text-sm text-slate-700">{f.nombre}</span>
              <span className="shrink-0 text-sm text-slate-500">
                <span className="font-medium text-slate-900">{f.visitas}</span>{" "}
                atenciones · {soles(f.monto)}
              </span>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
}

/**
 * LO QUE SE GANÓ Y NO HA ENTRADO.
 *
 * Dos agujeros distintos y por eso separados: una captura de Yape sin revisar
 * es trabajo pendiente de caja —el dinero probablemente esté—, y una atención
 * cerrada sin cobro es dinero que se fue por la puerta. Sumarlos en un solo
 * número escondería el segundo detrás del primero.
 */
function PendienteDeCobro({ datos }: { datos: PorCobrar }) {
  const limpio =
    datos.validacion_cantidad === 0 && datos.sin_pago_cantidad === 0;

  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Cuentas por cobrar</h2>
      </CardHeader>
      <CardBody className="space-y-3">
        {limpio ? (
          <p className="flex items-start gap-2 text-sm text-slate-600">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            Sin saldo pendiente: toda atención del periodo tiene pago
            asociado y no hay constancias por verificar.
          </p>
        ) : (
          <>
            <Pendiente
              etiqueta="Pendiente de validación"
              detalle={`${datos.validacion_cantidad} ${
                datos.validacion_cantidad === 1 ? "constancia" : "constancias"
              } de Yape o Plin sin verificar`}
              monto={datos.validacion_monto}
              cantidad={datos.validacion_cantidad}
            />
            <Pendiente
              etiqueta="Atenciones sin cobro registrado"
              detalle={`${datos.sin_pago_cantidad} ${
                datos.sin_pago_cantidad === 1 ? "atención cerrada" : "atenciones cerradas"
              } sin pago asociado`}
              monto={datos.sin_pago_monto}
              cantidad={datos.sin_pago_cantidad}
              alerta
            />
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Pendiente({
  etiqueta,
  detalle,
  monto,
  cantidad,
  alerta = false,
}: {
  etiqueta: string;
  detalle: string;
  monto: string;
  cantidad: number;
  alerta?: boolean;
}) {
  const hay = cantidad > 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm text-slate-600">
          {alerta && hay && (
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          )}
          {etiqueta}
        </span>
        <span
          className={`text-lg font-semibold ${
            !hay ? "text-slate-300" : alerta ? "text-amber-700" : "text-slate-900"
          }`}
        >
          {soles(monto)}
        </span>
      </div>
      <p className="text-xs text-slate-400">{hay ? detalle : "Sin saldo."}</p>
    </div>
  );
}

function variacion(actual: string, anterior: string): number | null {
  const a = Number(actual);
  const b = Number(anterior);
  // Sin base no hay porcentaje: "+∞ %" no es un dato, es un símbolo.
  if (!b) return null;
  return Math.round(((a - b) / b) * 100);
}

function Comparativa({
  icono,
  etiqueta,
  actual,
  anterior,
  variacion,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  actual: string;
  anterior: string;
  variacion: number | null;
}) {
  const sube = (variacion ?? 0) >= 0;
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2 text-slate-500">
          {icono}
          <p className="text-xs font-medium uppercase tracking-wide">{etiqueta}</p>
        </div>
        <p className="mt-2 text-3xl font-bold text-slate-900">{actual}</p>
        <div className="mt-1 flex items-center gap-2 text-sm">
          {variacion !== null && (
            <span
              className={`inline-flex items-center gap-1 font-medium ${
                sube ? "text-emerald-700" : "text-red-700"
              }`}
            >
              {sube ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              {sube ? "+" : ""}
              {variacion}%
            </span>
          )}
          <span className="text-slate-500">antes {anterior}</span>
        </div>
      </CardBody>
    </Card>
  );
}

function Dato({
  etiqueta,
  valor,
  alerta = false,
}: {
  etiqueta: string;
  valor: number;
  alerta?: boolean;
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">{etiqueta}</p>
      <p
        className={`text-xl font-semibold ${
          alerta && valor > 0 ? "text-red-700" : "text-slate-900"
        }`}
      >
        {valor}
      </p>
    </div>
  );
}

/**
 * Una barra proporcional.
 *
 * Con tres cifras en columna cuesta ver cuál pesa el doble que la otra; con la
 * barra se ve sin leer, que es para lo que se mira un panel.
 */
function Barra({
  nombre,
  valor,
  total,
  derecha,
}: {
  nombre: string;
  valor: number;
  total: number;
  derecha: string;
}) {
  const pct = total > 0 ? Math.round((valor / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-slate-700">{nombre}</span>
        <span className="font-mono text-slate-900">{derecha}</span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-slate-100">
        <div
          className="h-2 rounded-full bg-[color:var(--color-link)]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Ranking({ titulo, lineas }: { titulo: string; lineas: LineaReporte[] }) {
  const total = lineas.reduce((s, l) => s + Number(l.monto), 0);
  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">{titulo}</h2>
      </CardHeader>
      <CardBody className="space-y-3">
        {lineas.length === 0 && (
          <p className="text-sm text-slate-500">Sin datos en el periodo.</p>
        )}
        {lineas.map((l) => (
          <Barra
            key={l.nombre}
            // Atenciones cuando las hay —"a cuánta gente vio"— y si no, el
            // número de cobros. Son preguntas distintas y la etiqueta lo dice.
            nombre={
              l.atenciones > 0
                ? `${l.nombre} · ${l.atenciones} atenciones`
                : `${l.nombre} · ${l.cantidad} cobros`
            }
            valor={Number(l.monto)}
            total={total}
            derecha={soles(l.monto)}
          />
        ))}
      </CardBody>
    </Card>
  );
}
